import { defineMessages } from 'react-intl';

import { listItemMsg } from '@/shared/components/FormattedListValue';
import dateFormatService from './DateFormatService';

const messages = defineMessages({
  DefaultAssignment: 'Default Assignment',
  days: 'days',
  WeeklyEvery: 'WeeklyEvery',
  Weeks: 'Weeks',
});

export const mapResourceName = (
  resource,
  intl,
  withoutCode = false,
  withoutCategoryPrefix = false,
) => {
  if (!resource) return '';
  const codePrefix = resource.code && !withoutCode ? `${resource.code} – ` : '';
  if (resource.categoryId && !withoutCategoryPrefix) {
    const categoryName = intl.formatMessage(
      listItemMsg('resourcecategory', resource.categoryId, resource.categoryName),
    );
    if (resource.isDefault) return `${codePrefix}${categoryName}`;
    return `${codePrefix}${categoryName}: ${intl.formatMessage(
      listItemMsg('resource', resource.id ?? resource.resourceId, resource.name),
    )}`;
  }
  return `${codePrefix}${intl.formatMessage(
    listItemMsg('resource', resource.id ?? resource.resourceId, resource.name),
  )}`;
};

export const mapInvoiceStreamName = (stream, intl, defaultName = undefined) => {
  if (!stream) return '';
  if (!stream.assignmentId)
    return typeof defaultName !== 'undefined'
      ? defaultName
      : intl.formatMessage(messages.DefaultAssignment);

  if (!!stream.assignmentName || !stream.assignmentTemplateId) return stream.assignmentName;
  return intl.formatMessage(
    listItemMsg(
      'assignmenttemplate',
      stream.assignmentTemplateId,
      stream.assignmentTemplateName || stream.assignmentName,
    ),
  );
};

export const mapAssignmentGroupName = (
  assignmentTemplateId,
  assignmentTemplateName,
  assignmentName,
  intl,
) => {
  if (!assignmentTemplateId)
    return assignmentName || intl.formatMessage(messages.DefaultAssignment);

  if (assignmentName) return assignmentName;

  return intl.formatMessage(
    listItemMsg('assignmenttemplate', assignmentTemplateId, assignmentTemplateName),
  );
};

export const mapRegTaskName = (task, intl) => {
  if (!task || !task.plannedTaskId) return '';
  let taskName = task.plannedTaskName || '';
  if (!taskName && task.plannedTaskTemplateId) {
    taskName = intl.formatMessage(listItemMsg('taskTemplate', task.plannedTaskTemplateId));
  }
  if (!task.mainPlannedTaskId || task.mainPlannedTaskId === task.plannedTaskId) {
    return taskName;
  }

  let mainTaskName = task.mainPlannedTaskName || '';
  if (!mainTaskName && task.mainTaskTemplateId) {
    mainTaskName = intl.formatMessage(listItemMsg('taskTemplate', task.mainTaskTemplateId));
  }

  return mainTaskName ? `${mainTaskName} - ${taskName}` : taskName;
};

export const getShareItemNameForTask = (task, intl) => {
  if (!task) return null;
  const taskName =
    task.name ||
    intl.formatMessage(listItemMsg('tasktemplate', task.templateId, task.templateName));

  if (task.parent) {
    const parentName = getShareItemNameForTask(task.parent, intl);
    return parentName ? `${parentName} - ${taskName}` : taskName;
  }
  if (task.mainTaskId && task.mainTaskId !== task.id) {
    // tasklist
    if (task.mainTaskName) return `${task.mainTaskName} - ${taskName}`;
    if (task.mainTemplateId) {
      const parentName = intl.formatMessage(listItemMsg('tasktemplate', task.mainTemplateId));
      return parentName ? `${parentName} - ${taskName}` : taskName;
    }
  }

  return taskName;
};

const mapSuggestedMailAddressInternal = (x, translateListFunc) => {
  if (!x) return '';
  if (!x.emailAddress) return '';
  let label = x.emailAddress;
  if (x.relationName) {
    label += ` - ${x.relationName}`;
  }
  if (x.linkType !== null && translateListFunc) {
    const linkType = translateListFunc('linkTypeTo', x.linkType, '');
    if (linkType) label += ` (${linkType})`;
    if (x.isMain) {
      // Main address for link
      label += ' ☆';
    }
  } else if (x.isMain) {
    // Main address for main relation
    label += ' 🟊';
  }
  if (x.purposes && x.purposes.length > 0 && translateListFunc) {
    const purposes = x.purposes.map(p => translateListFunc('purpose', p, '')).join(', ');
    label += ` [${purposes}]`;
  }
  return label;
};
const intlTranslateFunc = intl => (list, id, fallback) =>
  intl.formatMessage(listItemMsg(list, id, fallback));

const angularFilterTranslateFunc = $filter => (list, id, fallback) =>
  $filter('translateList')(id, list, fallback);

export const mapSuggestedMailAddress = (address, intl) =>
  mapSuggestedMailAddressInternal(address, intlTranslateFunc(intl));

export const mapSuggestedMailAddressAngularFilter = (address, $filter) =>
  mapSuggestedMailAddressInternal(address, angularFilterTranslateFunc($filter));

export const getDeviationDays = (days, intl, showZero = false) => {
  if (days === 0 && !showZero) return '';
  return `${days >= 0 ? '+' : ''} ${days} ${intl.formatMessage(messages.days)}`;
};

export const getTaskPeriodicityLineDescription = (line, intl) => {
  const lineType = intl.formatMessage(listItemMsg('PeriodicityLineType', line.type));
  switch (line.type) {
    case 0: //Yearly
      return `${lineType} ${dateFormatService.formatDayMonth(line.date)}`;
    case 1: //Fixed
      return `${lineType} ${dateFormatService.formatDate(line.date)}`;
    case 4: //EndFiscalYear
    case 5: //NextGeneralMeeting
    case 6: //Birthday
    case 7: //ScheduleDate
    case 8: //IdCardExpiryDate
    case 9: //LeiExpiryDate
    case 11: //UboExpiryDate
      return `${lineType} ${getDeviationDays(line.deviation, intl)}`;
    case 10: //Weekly
      return `${lineType} - ${intl.formatMessage(messages.WeeklyEvery)} ${line.numberOfWeeks ??
        1} ${intl.formatMessage(messages.Weeks)} - ${intl.formatMessage(
        listItemMsg('dayOfWeek', line.dayOfWeek),
      )}`;
    case 2: //Cit
    case 3: //AnnualAccount
    default:
      return lineType;
  }
};

export default {
  mapResourceName,
  mapInvoiceStreamName,
  mapRegTaskName,
  mapSuggestedMailAddress,
  mapSuggestedMailAddressAngularFilter,
};
